import ReactDOM from 'react-dom/client';
import App from './App';
import { BrowserRouter as Router } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TaskStatusProvider } from './contexts/TaskStatus';
import { Toaster, ToastBar, toast } from 'react-hot-toast';
import { StyledEngineProvider } from '@mui/material/styles';
// index.tsx (or your app entry)
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css'; // <-- needed for subtitle2
import '@fontsource/roboto/700.css';

// Global Styling and Fonts Import
import './styles/index.scss';

import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';

// Initialize Datadog RUM early in the application lifecycle
import { initializeDatadogRUM } from './utilities/datadog';
initializeDatadogRUM();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const theme = createTheme({
  // in your theme
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
  },
  typography: {
    fontFamily: `'Roboto', Arial, sans-serif`, // $font-family
    fontWeightLight: 300, // $font-weight-light
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700, // $font-weight-bold
  },
  palette: {
    // ===========================
    // Core brand palettes
    // ===========================
    primary: {
      lightest: '#004d721f',
      light: '#2b75a2', // $primary-light
      main: '#004d72', // $primary-main
      dark: '#003859', // $primary-dark
      contrastText: '#fcfeff', // $contrastText
    },
    secondary: {
      lightest: '#CFE8F7',
      light: '#47a5dc', // $secondary-light
      main: '#007eb9', // $secondary-main
      dark: '#005b8e', // $secondary-dark
      // (contrast text)
      contrastText: '#fcfeff', // $secondary-constrastText (typo kept from source)
    },

    // ===========================
    // Feedback palettes
    // ===========================
    error: {
      light: '#ef5350', // $error-light
      main: '#d32f2f', // $error-main
      dark: '#c62828', // $error-dark
      contrastText: '#fcfeff',
    },
    warning: {
      light: '#ff9800', // $warning-light
      main: '#ef6c00', // $warning-main
      dark: '#e65100', // $warning-dark
      contrastText: '#212121',
    },
    success: {
      light: '#4caf50', // $success-light
      main: '#2e7d32', // $success-main
      dark: '#1b5e20', // $success-dark
      contrastText: '#fcfeff',
    },
    info: {
      light: '#29b6f6', // $info-blue-400
      main: '#03a9f4', // $info-blue-500
      dark: '#0277bd', // $info-blue-800
      contrastText: '#fcfeff',
    },

    alert: {
      success: {
        background: '#E8F5E9',
        color: '#1B5E20',
      },
      info: {
        background: '#E1F5FE',
        color: '#01579B',
      },
      warning: {
        background: '#FFF3E0',
        color: '#663C00',
      },
      error: {
        background: '#FEEBEE',
        color: '#5F2120',
      },
    },

    // ===========================
    // Text & surfaces
    // ===========================
    text: {
      primary: '#000000de', // $primary rgba(0, 0, 0, 0.87)
      secondary: '#00000061', // $secondary rgba(0, 0, 0, 0.38)
      disabled: '#00000038', // $disabled
    },
    background: {
      default: '#fcfeff', // $background
      paper: '#F3F7F7', // matches comment: paper-elevation-0/2/16/24, rgba(0, 0, 0, 0.04)
    },

    // ===========================
    // Neutrals & common
    // ===========================
    grey: {
      50: '#fafafa', // $grey-50
      100: '#f5f5f5', // $grey-100
      200: '#eeeeee', // $grey-200
      300: '#e0e0e0', // $grey-300 closest to $grey_12
      400: '#bdbdbd', // $grey-400
      500: '#9e9e9e', // $grey-500
      600: '#757575', // $grey-600
      700: '#616161', // $grey-700
      800: '#424242', // $grey-800
      900: '#212121', // $grey-900
      A100: '#eceff1', // $blue-grey-50
      A200: '#cfd8dc', // $blue-grey-100
      A400: '#90a4ae', // $blye-grey-300
      A700: '#455a64', // $blue-grey-700
    },
    common: {
      black: '#000000', // corrected from $black (#0000 looked like a typo)
      white: '#fcfeff', // $white
    },
    divider: '#e0e0e0', // $grey-300

    // ===========================
    // Custom (optional) – your “neutrals” block
    // ===========================
    // You can access these via theme.palette.neutrals.* in JS,
    // or add module augmentation in TS (see below).
    // This mirrors the shape you shared.
    neutrals: {
      white: '#fcfeff',
      blue_gray: '#cfd8dc', // rgba(0, 77, 114, 0.08)
      gray_light_2: '#eeeeee', // $grey-200
      gray_light_3: '#00000099', // rgba(0, 0, 0, 0.60)
      gray_border: '#e3e5e5', // custom (kept from your example)
      black: '#1a1a1a', // from your example
      primary_blue: '#004d72', // $primary-main
    },

    // ===========================
    // Action state tuning
    // ===========================
    action: {
      // You provided derived tokens; wire them to standard opacities:
      // selectedOpacity: 0.08 (matches your $selected comment)
      // focusOpacity: 0.12 (matches your $focus comment)
      // focusVisibleOpacity: 0.3 (matches your $focusVisible comment)
      hoverOpacity: 0.07,
      selectedOpacity: 0.08,
      disabledOpacity: 0.38,
      disabledBackground: '#00000014', // $grey-08
      focusOpacity: 0.12,
      activatedOpacity: 0.12,
    },

    // ==========================
    // Accents
    // ==========================
    accent: {
      yellowBrand: {
        300: '#F5DB6B',
        500: '#F4C93C',
        700: '#F1AA2F',
      },
      blueBrand: {
        200: '#80BFF3',
        500: '#3D84B4',
      }
    },

  },
});

function AppWithProviders() {
  const { isLoading } = useAuth();

  if (isLoading) return null;

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <TaskStatusProvider>
          <App />
        </TaskStatusProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

root.render(
  <Router
    future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    }}
  >
    <AuthProvider>
      <AppWithProviders />
    </AuthProvider>
    <Toaster
      position='top-center'
      reverseOrder={false}
      containerStyle={{ zIndex: 20000 }}
      toastOptions={{
        duration: 2000,
        error: { duration: 4000 },
        success: { duration: 2000 },
      }}
    >
      {(t) => (
        <div onClick={() => toast.dismiss(t.id)} style={{ cursor: 'pointer' }}>
          <ToastBar toast={t} />
        </div>
      )}
    </Toaster>
  </Router>
);
